import * as Sentry from "@sentry/react";
import { isError, isString } from "es-toolkit/compat";

import { APP_BUILD_NUMBER, APP_VERSION } from "$src/core/constants/env";
import { getSentryOptions } from "$src/core/constants/sentry";
import type { Extra, LoggingService } from "$src/core/services/logging/logging";

const SENTRY_OPTIONS = getSentryOptions({
  isContinuousTelemetrySupported: true,
});

const buildRelease = () => `web@${APP_VERSION}+${APP_BUILD_NUMBER}`;

const init = ({
  dsn,
  environment,
}: {
  dsn: string | undefined;
  environment: string | undefined;
}) => {
  if (__DEV__) return;

  if (Sentry.getClient()) return;

  if (!dsn || !environment) {
    console.warn("Sentry config is missing");
    return;
  }

  Sentry.init({
    ...SENTRY_OPTIONS,
    dsn,
    environment,
    release: buildRelease(),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
    ],
  });
};

const toReported = (errorOrMessage: unknown, extra?: Extra) => {
  const extraError = isError(extra?.error) ? extra.error : undefined;

  if (isString(errorOrMessage)) {
    return extraError
      ? new Error(`${errorOrMessage}: ${extraError.message}`, {
          cause: extra?.error,
        })
      : new Error(errorOrMessage, { cause: extra?.error });
  }

  return errorOrMessage ?? extraError;
};

const captureError = (errorOrMessage: unknown, extra?: Extra) => {
  const error = toReported(errorOrMessage, extra);

  if (__DEV__) console.error(error, extra ?? "");

  Sentry.captureException(error, { level: "error", extra });
};

const captureWarning = (errorOrMessage: unknown, extra?: Extra) => {
  const error = toReported(errorOrMessage, extra);

  if (__DEV__) console.warn(error, extra ?? "");

  Sentry.captureException(error, { level: "warning", extra });
};

const captureInfo = (message: string, extra?: Extra) => {
  if (__DEV__) console.info(message, extra ?? "");

  Sentry.captureMessage(message, { level: "info", extra });
};

const captureDebug = (message: string, extra?: Extra) => {
  if (__DEV__) console.debug(message, extra ?? "");
};

const SentryService: LoggingService = {
  init,
  error: captureError,
  warn: captureWarning,
  info: captureInfo,
  debug: captureDebug,
};

export default SentryService;
